import React, { useState, useEffect } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  DataTable,
  Spinner,
  Box,
} from '@shopify/polaris';
import { experimentApi } from '../utils/api';

const CONFIDENCE_TONE = {
  low: 'attention',
  medium: 'warning',
  high: 'success',
};

function ExperimentResultsCard({ experiment, startDate, endDate }) {
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      setIsLoading(true);
      try {
        const data = await experimentApi.getResults(experiment.id, startDate, endDate);
        setResults(data);
      } catch (err) {
        console.error('Failed to fetch experiment results:', err);
      }
      setIsLoading(false);
    };
    fetchResults();
  }, [experiment.id, startDate, endDate]);

  if (isLoading) {
    return (
      <Card>
        <Box padding="400">
          <InlineStack align="center">
            <Spinner size="small" />
          </InlineStack>
        </Box>
      </Card>
    );
  }

  if (!results || !results.variants) {
    return null;
  }

  const rows = results.variants.map(v => [
    v.bar_name,
    v.impressions.toLocaleString(),
    v.clicks.toLocaleString(),
    v.conversions.toLocaleString(),
    `${v.conversion_rate}%`,
    results.winner_bar_id === v.bar_id ? (
      <Badge tone="success">Winner</Badge>
    ) : '',
  ]);

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack gap="200" blockAlign="center">
          <Text variant="headingSm">{experiment.name}</Text>
          <Badge tone={experiment.status === 'running' ? 'success' : undefined}>
            {experiment.status.charAt(0).toUpperCase() + experiment.status.slice(1)}
          </Badge>
          {experiment.target_country && (
            <Badge>{experiment.target_country}</Badge>
          )}
        </InlineStack>

        <DataTable
          columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'text']}
          headings={['Bar', 'Impressions', 'Clicks', 'Conversions', 'Conv. Rate', '']}
          rows={rows}
        />

        <InlineStack gap="200" blockAlign="center">
          <Text tone="subdued" variant="bodySm">Confidence:</Text>
          <Badge tone={CONFIDENCE_TONE[results.confidence] || 'default'}>
            {results.confidence ? results.confidence.charAt(0).toUpperCase() + results.confidence.slice(1) : 'Unknown'}
          </Badge>
          {results.confidence === 'low' && (
            <Text tone="subdued" variant="bodySm">
              More data needed for reliable results
            </Text>
          )}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export default ExperimentResultsCard;
